// Archiving an integration, and the Shared Settings it leaves behind: those
// linking only two integrations are deleted, the rest are updated in place.

import { useCallback, useState } from 'react';
import { t } from '@lingui/macro';

import IntegrationsApi from '../../../api/integrations';
import SharedSettingsApi from '../../../api/shared_settings';

// A shared setting that only links two integrations has nothing left to link
// once this one goes, so it is deleted rather than updated.
const MIN_LINKED_INTEGRATIONS = 2;

export default function useIntegrationArchive({
  integration,
  notify,
  navigate,
}) {
  const [visible, setVisible] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [toDelete, setToDelete] = useState([]);
  const [toUpdate, setToUpdate] = useState([]);

  const open = useCallback(async () => {
    const { data } = await SharedSettingsApi.fetchAllSharedSettings();
    const affected = (data || []).filter((item) =>
      (item.attributes?.integration_uuids || []).includes(integration?.id),
    );

    setToDelete(
      affected.filter(
        (item) =>
          (item.attributes?.integration_uuids || []).length <=
          MIN_LINKED_INTEGRATIONS,
      ),
    );
    setToUpdate(
      affected.filter(
        (item) =>
          (item.attributes?.integration_uuids || []).length >
          MIN_LINKED_INTEGRATIONS,
      ),
    );
    setVisible(true);
  }, [integration?.id]);

  const close = useCallback(() => {
    setToDelete([]);
    setToUpdate([]);
    setVisible(false);
  }, []);

  const archive = useCallback(async () => {
    setArchiving(true);

    if (toDelete.length > 0) {
      await Promise.all(
        toDelete.map((s) => SharedSettingsApi.deleteSharedSetting(s.id)),
      );
    }

    if (toUpdate.length > 0) {
      await Promise.all(
        toUpdate.map((s) =>
          SharedSettingsApi.updateSharedSetting(s.id, {
            ...s.attributes,
            integration_uuids: (s.attributes?.integration_uuids || []).filter(
              (uuid) => uuid !== integration?.id,
            ),
          }),
        ),
      );
    }

    const response = await IntegrationsApi.archiveIntegration(integration?.id);

    navigate(`/integrations`);

    if (response.status === 204) {
      notify(
        'success',
        t`Integration Archived`,
        t`${integration.attributes.name} has been archived`,
      );
    } else {
      notify(
        'danger',
        t`Something went wrong!`,
        t`Attempt to archive ${integration.attributes.name} failed.`,
      );
    }

    setArchiving(false);
  }, [toDelete, toUpdate, integration, notify]);

  return { visible, archiving, toDelete, toUpdate, open, close, archive };
}
